import { useCallback } from "react";
import { createSessionStore } from "@/hooks/session-storage/create-session-store";

type CountsKey = "sms" | "chat";

type Count = Record<CountsKey, number> | undefined;

const fcmCountsStore = createSessionStore<Count>("fcm-counts", undefined);

type Action<T extends number | Count> = [
  T,
  (key: CountsKey, value: number) => void,
];

/**
 * Hook to manage the fcm counts for Multiple Events
 * @description count is the number of notifications received from firebase
 *
 * @returns [count, setCount]
 *
 * @author Rajkumar Rathod
 */
const useFcmCounts = <T extends number | Count>(key?: CountsKey): Action<T> => {
  const [count, setCount] = fcmCountsStore.useStore();

  const setCountFn = useCallback(
    (key: CountsKey, value: number) => {
      const clonedCount = { ...count };
      clonedCount[key] = value ?? 0;
      setCount(clonedCount as Count);
    },
    [count]
  );

  return [key ? (count?.[key] as T) : (count as T), setCountFn];
};

export default useFcmCounts;
